import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useAuthStore } from '../../stores/auth.store';
import { fmtEur } from '../../lib/utils';
import {
  dealOwnersQuery, pipelinesQuery, CANONE_MENSILE, SCENARI, SCENARIO_LABEL,
  fullName, pathStages, ricavoVoucher, type ScenarioKey,
} from '../../features/deals/dealsApi';

/**
 * Nuova trattativa: il minimo per poterla chiamare domani.
 *
 * Prima si sceglie la pipeline (voucher, rinnovi, estensioni, nuovi clienti):
 * da lì dipendono gli step e i campi. Nel voucher non si digita nessun importo,
 * si sceglie la configurazione (20k o 40k) e il ricavo lo calcola il sistema;
 * nelle altre pipeline c'è l'importo libero. Salva con Ctrl/Cmd+Invio.
 */
export function NewDealModal({ onClose, pipeline: pipelineSlug }: { onClose: () => void; pipeline?: string }) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const { data: pipelines } = useQuery(pipelinesQuery);
  const { data: owners } = useQuery(dealOwnersQuery);

  const [form, setForm] = useState({
    pipeline: pipelineSlug ?? '',
    stageId: '',
    companyName: '',
    contactName: '',
    phone: '',
    scenario: '' as '' | ScenarioKey,
    mesiCanone: 24,
    amount: '',
    fascia: '',
    expectedCloseDate: '',
    ownerId: me?.id ?? '',
  });

  const current = useMemo(() => (pipelines ?? []).find((p) => p.slug === form.pipeline), [pipelines, form.pipeline]);
  const stages = pathStages(current);

  // Alla prima pipeline disponibile, primo step: il form non resta mai a vuoto
  useEffect(() => {
    if (!pipelines?.length) return;
    const p = (pipelines ?? []).find((x) => x.slug === form.pipeline) ?? pipelines[0]!;
    const first = pathStages(p)[0];
    if (form.pipeline !== p.slug || !form.stageId || !p.stages.some((s) => s.id === form.stageId)) {
      setForm((f) => ({ ...f, pipeline: p.slug, stageId: first?.id ?? '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelines, form.pipeline]);

  const isVoucher = current?.kind === 'VOUCHER';
  const ricavo = isVoucher && form.scenario ? ricavoVoucher(form.scenario, form.mesiCanone) : null;

  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>('/deals', {
      title: form.companyName.trim(),
      companyName: form.companyName.trim(),
      contactName: form.contactName.trim() || null,
      phone: form.phone.trim() || null,
      pipeline: form.pipeline,
      stageId: form.stageId,
      scenario: isVoucher ? form.scenario || null : null,
      mesiCanone: form.mesiCanone,
      amount: isVoucher ? 0 : Number(form.amount.replace(',', '.')) || 0,
      fascia: form.fascia || null,
      expectedCloseDate: form.expectedCloseDate || null,
      ownerId: form.ownerId || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      toast.ok('Trattativa creata', form.companyName);
      onClose();
    },
    onError: (err) => toast.alert('Trattativa non creata', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const canSubmit = !!form.companyName.trim() && !!form.stageId && !create.isPending;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) { e.preventDefault(); create.mutate(); }
  };

  return (
    <Modal
      title="Nuova trattativa"
      kicker="Trattative"
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!canSubmit}>
            Crea trattativa <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid" onKeyDown={onKeyDown}>
        <Field label="Pipeline" span={7} hint="Da qui dipendono gli step">
          <select className="inp" value={form.pipeline} onChange={(e) => setForm({ ...form, pipeline: e.target.value, stageId: '' })}>
            {(pipelines ?? []).map((p) => <option key={p.id} value={p.slug}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Step" span={5}>
          <select className="inp" value={form.stageId} onChange={(e) => setForm({ ...form, stageId: e.target.value })}>
            {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>

        <Field label="Azienda" required span={12}>
          <input className="inp" autoFocus placeholder="Ragione sociale" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} />
        </Field>
        <Field label="Persona" span={6} hint="Chi si chiama">
          <input className="inp" placeholder="Nome e cognome" value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
        </Field>
        <Field label="Telefono" span={6}>
          <input className="inp mono" placeholder="Numero da chiamare" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>

        {isVoucher ? (
          <>
            <Field label="Configurazione" span={6} hint="50% a fondo perduto">
              <select className="inp" value={form.scenario} onChange={(e) => setForm({ ...form, scenario: e.target.value as ScenarioKey })}>
                <option value="">Da definire</option>
                {SCENARI.map((s) => <option key={s} value={s}>{SCENARIO_LABEL[s]}</option>)}
              </select>
            </Field>
            <Field label="Mesi di canone" span={3} hint={`${CANONE_MENSILE} € al mese`}>
              <input className="inp mono" type="number" min={0} max={120} value={form.mesiCanone} onChange={(e) => setForm({ ...form, mesiCanone: Number(e.target.value) })} />
            </Field>
            <Field label="Ricavo per noi" span={3} hint="istruttoria + fee + canone">
              <div className="inp flex items-center mono" style={{ background: 'var(--paper-2)' }}>
                {ricavo != null ? `€ ${fmtEur(ricavo, true)}` : '—'}
              </div>
            </Field>
          </>
        ) : (
          <Field label="Importo" span={6} hint="Quanto ci aspettiamo di fatturare">
            <input className="inp mono" inputMode="decimal" placeholder="0,00" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </Field>
        )}

        <Field label="Fascia" span={4} hint="Priorità di chiamata">
          <select className="inp" value={form.fascia} onChange={(e) => setForm({ ...form, fascia: e.target.value })}>
            <option value="">Nessuna</option>
            {['A', 'B', 'C', 'D'].map((f) => <option key={f} value={f}>Fascia {f}</option>)}
          </select>
        </Field>
        <Field label="Chiusura prevista" span={4}>
          <input className="inp mono" type="date" value={form.expectedCloseDate} onChange={(e) => setForm({ ...form, expectedCloseDate: e.target.value })} />
        </Field>
        <Field label="Chi segue" span={4}>
          <select className="inp" value={form.ownerId} onChange={(e) => setForm({ ...form, ownerId: e.target.value })}>
            <option value="">Nessuno</option>
            {(owners ?? []).map((u) => <option key={u.id} value={u.id}>{u.id === me?.id ? 'Io' : fullName(u)}</option>)}
          </select>
        </Field>
      </div>
    </Modal>
  );
}
